import { useCallback } from "react";

import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { FileAttributes } from "src/clients/files";
import {
  FileDownloadButton,
  FileViewerBody,
  FileViewerTitle,
  getContentType,
  getFileGroup,
  PLACEHOLDER_FILE,
} from "src/files/FileViewer";

export type FileViewerWindowData = {
  /** `undefined` for a file whose record does not exist yet. */
  file: FileAttributes | File | undefined;
};

/**
 * The file viewer as a window the floating-window store owns, opened by a
 * `FileViewer` running in `shared` mode. Chromed like the inline viewer, and
 * unpinnable for the same reason: closing it is the only lifecycle the viewer
 * offers. What it adds is a lifetime independent of the trigger that opened
 * it, so a surface that re-creates its triggers cannot take it down.
 */
export const FileViewerWindow: React.FC<FloatingWindowProps> = ({
  id,
  computePosition,
  data,
}) => {
  const { file } = data as FileViewerWindowData;
  const { close } = useFloatingWindowsActions();
  const { position, windowRef } = useFloatingWindowPosition(computePosition);

  const handleClose = useCallback((): void => close(id), [close, id]);

  const displayedFile = file ?? PLACEHOLDER_FILE;
  const isFilePDF = getFileGroup(getContentType(displayedFile)) === "pdf";

  return (
    <FloatingWindowInternal
      ref={windowRef}
      height="h-144"
      id={id}
      isPinned={false}
      minHeight="h-64"
      minWidth="w-96"
      style={{
        inset: "0px auto auto 0px",
        transform: position,
      }}
      title={<FileViewerTitle file={displayedFile} />}
      titleRight={
        isFilePDF ? null : <FileDownloadButton file={displayedFile} />
      }
      width="w-176"
      draggable
      maximizable
      resizeable
      onClose={handleClose}
    >
      <FileViewerBody file={file} />
    </FloatingWindowInternal>
  );
};
